'use client';

import { Slot } from '@magicschool/spellbook/components/Slot';
import { cn } from '@magicschool/spellbook/lib/utils';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { createContext, useContext } from 'react';

export const ChatMessageVariantContext = createContext<'user' | 'assistant'>('user');

export const chatMessageContentVariants = cva(['relative', 'min-w-0'], {
  variants: {
    /** `user` draws the bubble; `assistant` is unbubbled and full width. Taken from the enclosing ChatMessage unless set. */
    variant: {
      user: ['flex', 'flex-col', 'justify-center', 'min-h-10', 'rounded-card-md', 'px-3', 'py-2'],
      assistant: ['w-full'],
    },
    /** Moderation state of a user bubble: `non-urgent` outlines it in warning, `urgent` in error. */
    tone: {
      default: [],
      'non-urgent': ['bg-surface-background-0', 'border-2', 'border-warning-muted-stroke'],
      urgent: ['bg-surface-background-0', 'border-2', 'border-error-muted-stroke'],
    },
    /** `default` caps a user bubble at `max-w-lg`; `full` lets it grow to the thread's width. */
    width: {
      default: [],
      full: ['max-w-full'],
    },
  },
  compoundVariants: [
    { variant: 'user', tone: 'default', className: 'bg-default-muted-background' },
    { variant: 'user', width: 'default', className: 'max-w-lg' },
  ],
  defaultVariants: { tone: 'default', width: 'default' },
});

export type ChatMessageContentProps = ComponentProps<'div'> & VariantProps<typeof chatMessageContentVariants>;

const ChatMessageContentBase = ({ variant, tone, width, className, ...props }: ChatMessageContentProps) => {
  const inheritedVariant = useContext(ChatMessageVariantContext);
  return (
    <div
      data-spellbook-name="chat-message-content"
      data-tone={tone ?? 'default'}
      className={cn(chatMessageContentVariants({ variant: variant ?? inheritedVariant, tone, width }), className)}
      {...props}
    />
  );
};

export const ChatMessageContentSlot = Slot<ChatMessageContentProps>({
  displayName: 'ChatMessage.Content',
  preventOverride: true,
  renderDefault: (props) => <ChatMessageContentBase {...props} />,
});

export type ChatMessageActionsProps = ComponentProps<'div'> & {
  /** Hides the actions until the message is hovered or focused. Set `false` on the latest message so its actions stay visible. Always shown on touch screens. @default true */
  autohide?: boolean;
};

export const ChatMessageActionsSlot = Slot<ChatMessageActionsProps>({
  displayName: 'ChatMessage.Actions',
  preventOverride: true,
  renderDefault: ({ autohide = true, className, ...props }) => (
    <div
      data-spellbook-name="chat-message-actions"
      data-autohide={autohide || undefined}
      className={cn(
        'flex',
        'items-center',
        'gap-0.5',
        'mt-1',
        'empty:hidden',
        autohide && [
          'opacity-0',
          'transition-opacity',
          'group-hover/chat-message:opacity-100',
          'group-focus-within/chat-message:opacity-100',
          'has-[[data-state=open]]:opacity-100',
          'pointer-coarse:opacity-100',
        ],
        className,
      )}
      {...props}
    />
  ),
});

export const ChatMessageAttachmentsSlot = Slot<ComponentProps<'div'>>({
  displayName: 'ChatMessage.Attachments',
  preventOverride: true,
  renderDefault: ({ className, ...props }) => (
    <div
      data-spellbook-name="chat-message-attachments"
      className={cn('flex', 'flex-wrap', 'items-center', 'gap-1', 'pb-1', className)}
      {...props}
    />
  ),
});
